/**
 * Three-CSG - Constructive Solid Geometry for Three.js
 * Simplified implementation for CAD boolean operations
 * Based on CSG.js concepts
 */

class CSG {
  constructor() {
    this.polygons = [];
  }

  clone() {
    const csg = new CSG();
    csg.polygons = this.polygons.map(p => p.clone());
    return csg;
  }

  toPolygons() {
    return this.polygons;
  }

  /**
   * Return a new CSG solid representing space in either this solid or in the
   * solid `csg`. Neither this solid nor the solid `csg` are modified.
   */
  union(csg) {
    const a = new CSGNode(this.clone().polygons);
    const b = new CSGNode(csg.clone().polygons);
    a.clipTo(b);
    b.clipTo(a);
    b.invert();
    b.clipTo(a);
    b.invert();
    a.build(b.allPolygons());
    return CSG.fromPolygons(a.allPolygons());
  }

  /**
   * Return a new CSG solid representing space in this solid but not in the
   * solid `csg`. Neither this solid nor the solid `csg` are modified.
   */
  subtract(csg) {
    const a = new CSGNode(this.clone().polygons);
    const b = new CSGNode(csg.clone().polygons);
    a.invert();
    a.clipTo(b);
    b.clipTo(a);
    b.invert();
    b.clipTo(a);
    b.invert();
    a.build(b.allPolygons());
    a.invert();
    return CSG.fromPolygons(a.allPolygons());
  }

  /**
   * Return a new CSG solid representing space both this solid and in the
   * solid `csg`. Neither this solid nor the solid `csg` are modified.
   */
  intersect(csg) {
    const a = new CSGNode(this.clone().polygons);
    const b = new CSGNode(csg.clone().polygons);
    a.invert();
    b.clipTo(a);
    b.invert();
    a.clipTo(b);
    b.clipTo(a);
    a.build(b.allPolygons());
    a.invert();
    return CSG.fromPolygons(a.allPolygons());
  }

  /**
   * Return a new CSG solid with solid and empty space switched.
   */
  inverse() {
    const csg = this.clone();
    csg.polygons.forEach(p => p.flip());
    return csg;
  }

  /**
   * Convert Three.js BufferGeometry to CSG
   */
  static fromMesh(mesh) {
    const csg = new CSG();
    const geometry = mesh.geometry;

    if (!geometry.isBufferGeometry) {
      console.error('Geometry must be BufferGeometry');
      return csg;
    }

    const positionAttribute = geometry.getAttribute('position');
    const normalAttribute = geometry.getAttribute('normal');
    const index = geometry.index;

    const vertices = [];
    for (let i = 0; i < positionAttribute.count; i++) {
      vertices.push(new CSGVertex(
        new THREE.Vector3().fromBufferAttribute(positionAttribute, i),
        new THREE.Vector3().fromBufferAttribute(normalAttribute, i)
      ));
    }

    const polygons = [];
    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        const polygon = new CSGPolygon([
          vertices[index.getX(i)],
          vertices[index.getX(i + 1)],
          vertices[index.getX(i + 2)]
        ]);
        polygons.push(polygon);
      }
    } else {
      for (let i = 0; i < vertices.length; i += 3) {
        const polygon = new CSGPolygon([
          vertices[i],
          vertices[i + 1],
          vertices[i + 2]
        ]);
        polygons.push(polygon);
      }
    }

    csg.polygons = polygons;
    return csg;
  }

  /**
   * Convert CSG to Three.js BufferGeometry
   */
  toMesh(material) {
    const geometry = this.toGeometry();
    return new THREE.Mesh(geometry, material);
  }

  toGeometry() {
    const vertices = [];
    const normals = [];
    const indices = [];
    let index = 0;

    this.polygons.forEach(polygon => {
      const firstVertex = polygon.vertices[0];
      for (let i = 2; i < polygon.vertices.length; i++) {
        [firstVertex, polygon.vertices[i - 1], polygon.vertices[i]].forEach(v => {
          vertices.push(v.pos.x, v.pos.y, v.pos.z);
          normals.push(v.normal.x, v.normal.y, v.normal.z);
          indices.push(index++);
        });
      }
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geometry.setIndex(indices);
    geometry.computeBoundingSphere();

    return geometry;
  }

  static fromPolygons(polygons) {
    const csg = new CSG();
    csg.polygons = polygons;
    return csg;
  }
}

/**
 * CSG Vertex
 */
class CSGVertex {
  constructor(pos, normal) {
    this.pos = pos.clone();
    this.normal = normal.clone();
  }

  clone() {
    return new CSGVertex(this.pos, this.normal);
  }

  flip() {
    this.normal.negate();
  }

  interpolate(other, t) {
    return new CSGVertex(
      this.pos.clone().lerp(other.pos, t),
      this.normal.clone().lerp(other.normal, t)
    );
  }
}

/**
 * CSG Polygon
 */
class CSGPolygon {
  constructor(vertices, shared) {
    this.vertices = vertices;
    this.shared = shared;
    this.plane = CSGPlane.fromPoints(vertices[0].pos, vertices[1].pos, vertices[2].pos);
  }

  clone() {
    return new CSGPolygon(this.vertices.map(v => v.clone()), this.shared);
  }

  flip() {
    this.vertices.reverse().forEach(v => v.flip());
    this.plane.flip();
  }
}

/**
 * CSG Plane
 */
class CSGPlane {
  constructor(normal, w) {
    this.normal = normal;
    this.w = w;
  }

  clone() {
    return new CSGPlane(this.normal.clone(), this.w);
  }

  flip() {
    this.normal.negate();
    this.w = -this.w;
  }

  static fromPoints(a, b, c) {
    const n = b.clone().sub(a).cross(c.clone().sub(a)).normalize();
    return new CSGPlane(n, n.dot(a));
  }

  splitPolygon(polygon, coplanarFront, coplanarBack, front, back) {
    const COPLANAR = 0;
    const FRONT = 1;
    const BACK = 2;
    const SPANNING = 3;
    const EPSILON = 1e-5;

    let polygonType = 0;
    const types = [];

    for (let i = 0; i < polygon.vertices.length; i++) {
      const t = this.normal.dot(polygon.vertices[i].pos) - this.w;
      const type = (t < -EPSILON) ? BACK : (t > EPSILON) ? FRONT : COPLANAR;
      polygonType |= type;
      types.push(type);
    }

    switch (polygonType) {
      case COPLANAR:
        (this.normal.dot(polygon.plane.normal) > 0 ? coplanarFront : coplanarBack).push(polygon);
        break;
      case FRONT:
        front.push(polygon);
        break;
      case BACK:
        back.push(polygon);
        break;
      case SPANNING:
        const f = [];
        const b = [];
        for (let i = 0; i < polygon.vertices.length; i++) {
          const j = (i + 1) % polygon.vertices.length;
          const ti = types[i];
          const tj = types[j];
          const vi = polygon.vertices[i];
          const vj = polygon.vertices[j];

          if (ti != BACK) f.push(vi);
          if (ti != FRONT) b.push(ti != BACK ? vi.clone() : vi);

          if ((ti | tj) == SPANNING) {
            const t = (this.w - this.normal.dot(vi.pos)) / this.normal.dot(vj.pos.clone().sub(vi.pos));
            const v = vi.interpolate(vj, t);
            f.push(v);
            b.push(v.clone());
          }
        }
        if (f.length >= 3) front.push(new CSGPolygon(f, polygon.shared));
        if (b.length >= 3) back.push(new CSGPolygon(b, polygon.shared));
        break;
    }
  }
}

/**
 * CSG Node (BSP Tree)
 */
class CSGNode {
  constructor(polygons) {
    this.plane = null;
    this.front = null;
    this.back = null;
    this.polygons = [];
    if (polygons) this.build(polygons);
  }

  clone() {
    const node = new CSGNode();
    node.plane = this.plane && this.plane.clone();
    node.front = this.front && this.front.clone();
    node.back = this.back && this.back.clone();
    node.polygons = this.polygons.map(p => p.clone());
    return node;
  }

  invert() {
    for (let i = 0; i < this.polygons.length; i++) {
      this.polygons[i].flip();
    }
    if (this.plane) this.plane.flip();
    if (this.front) this.front.invert();
    if (this.back) this.back.invert();
    const temp = this.front;
    this.front = this.back;
    this.back = temp;
  }

  clipPolygons(polygons) {
    if (!this.plane) return polygons.slice();
    let front = [];
    let back = [];
    for (let i = 0; i < polygons.length; i++) {
      this.plane.splitPolygon(polygons[i], front, back, front, back);
    }
    if (this.front) front = this.front.clipPolygons(front);
    if (this.back) back = this.back.clipPolygons(back);
    else back = [];
    return front.concat(back);
  }

  clipTo(bsp) {
    this.polygons = bsp.clipPolygons(this.polygons);
    if (this.front) this.front.clipTo(bsp);
    if (this.back) this.back.clipTo(bsp);
  }

  allPolygons() {
    let polygons = this.polygons.slice();
    if (this.front) polygons = polygons.concat(this.front.allPolygons());
    if (this.back) polygons = polygons.concat(this.back.allPolygons());
    return polygons;
  }

  build(polygons) {
    if (!polygons.length) return;
    if (!this.plane) this.plane = polygons[0].plane.clone();
    const front = [];
    const back = [];
    for (let i = 0; i < polygons.length; i++) {
      this.plane.splitPolygon(polygons[i], this.polygons, this.polygons, front, back);
    }
    if (front.length) {
      if (!this.front) this.front = new CSGNode();
      this.front.build(front);
    }
    if (back.length) {
      if (!this.back) this.back = new CSGNode();
      this.back.build(back);
    }
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CSG };
}
